/**
 * Scroll depth for GA4 (analyst request 2026-10-08): `scroll_depth` event with
 * `percent_scrolled` in 10% steps, once per step per page view.
 *
 * GA4's enhanced-measurement "scroll" fires at 90% of `window` only. This app
 * scrolls inside `main.scroll` / `.chat-scroll`, so window never scrolls and that
 * event never fires. We listen in capture phase on `document`, which sees scroll
 * events from every scrolling element, and measure the element that scrolled.
 *
 * Pure helpers (no DOM, no Firebase) are exported for tests.
 */

export const SCROLL_STEPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/** 0..100, rounded down. 100 when the content does not scroll at all (fully visible). */
export function scrollPercent({ scrollTop, scrollHeight, clientHeight }) {
  const max = scrollHeight - clientHeight;
  if (!(max > 0)) return 100;
  const pct = Math.floor(((scrollTop + clientHeight) / scrollHeight) * 100);
  return Math.max(0, Math.min(100, pct));
}

/** Steps not yet in `reached` that `percent` now covers, ascending. */
export function newSteps(percent, reached) {
  return SCROLL_STEPS.filter((s) => s <= percent && !reached.has(s));
}

/**
 * Per-page tracker. `send(step)` is called once per step; `reset()` on route change.
 * The 100% step is not auto-reported for non-scrolling pages (nothing was scrolled).
 */
export function createScrollDepthTracker(send) {
  let reached = new Set();
  return {
    reset() {
      reached = new Set();
    },
    observe(metrics) {
      if (!(metrics.scrollHeight - metrics.clientHeight > 0)) return [];
      const steps = newSteps(scrollPercent(metrics), reached);
      steps.forEach((s) => {
        reached.add(s);
        send(s);
      });
      return steps;
    },
  };
}

/** Attach to the document; returns a detach function. */
export function attachScrollDepth(tracker, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return () => {};
  let scheduled = false;
  let pending = null;
  const onScroll = (ev) => {
    const el = ev.target === doc ? doc.scrollingElement || doc.documentElement : ev.target;
    if (!el || typeof el.scrollTop !== 'number') return;
    pending = el;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const t = pending;
      pending = null;
      if (t) tracker.observe({ scrollTop: t.scrollTop, scrollHeight: t.scrollHeight, clientHeight: t.clientHeight });
    });
  };
  doc.addEventListener('scroll', onScroll, { capture: true, passive: true });
  return () => doc.removeEventListener('scroll', onScroll, { capture: true });
}
